'use client';

import { cn } from '@core/utils';

import { BREAKPOINTS } from '@/constants';
import { AD_MOCK_MODE, isAdHidden, useAd } from '@/hooks/useAd';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { AdBadge } from './AdBadge';
import {
  AD_SLOT_DEBUG_PLACEHOLDER_ENABLED,
  AdSlotDebugPlaceholder,
} from './AdSlotDebugPlaceholder';
import { getAdSlotFrameProps } from './adSlotFrame';

export interface NativeAdSlotConfig {
  //* 빈 string은 콘솔 ID 미발급 슬롯 — 운영에서는 null, 개발에서는 위치 확인용 placeholder.
  unitIdM: string;
  unitIdP: string;
  mWidth: number;
  mHeight: number;
  pWidth: number;
  pHeight: number;
  rounded?: 'medium' | 'large';
}

interface NativeAdSlotProps extends NativeAdSlotConfig {
  as?: 'div' | 'li';
  className?: string;
}

export const NativeAdSlot = ({
  unitIdM,
  unitIdP,
  mWidth,
  mHeight,
  pWidth,
  pHeight,
  as: FrameElement = 'div',
  className,
  rounded = 'large',
}: NativeAdSlotProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const unitId = isDesktop ? unitIdP : unitIdM;
  const width = isDesktop ? pWidth : mWidth;
  const height = isDesktop ? pHeight : mHeight;

  const { containerRef, adState } = useAd(unitId, { type: 'native' });
  const isSlotHidden = isAdHidden(adState);

  if (
    !AD_SLOT_DEBUG_PLACEHOLDER_ENABLED &&
    (isSlotHidden || (!unitIdM && !unitIdP))
  ) {
    return null;
  }

  const frame = getAdSlotFrameProps({
    unitIdM,
    unitIdP,
    mWidth,
    mHeight,
    pWidth,
    pHeight,
    showEmptyUnitPlaceholder: AD_SLOT_DEBUG_PLACEHOLDER_ENABLED,
  });

  return (
    <FrameElement
      className={cn('relative', frame.className, className)}
      style={frame.style}
    >
      {!unitId || isSlotHidden ? (
        AD_SLOT_DEBUG_PLACEHOLDER_ENABLED && (
          <AdSlotDebugPlaceholder rounded={rounded} className="size-full" />
        )
      ) : (
        <>
          {/* 컨테이너 밖에 둔다 — 백필 렌더 시 SDK가 컨테이너 innerHTML을 비우므로 */}
          <AdBadge className={cn(adState !== 'adReceived' && 'invisible')} />
          <div
            key={unitId}
            ref={containerRef}
            className={cn(
              'size-full overflow-hidden',
              rounded === 'medium' ? 'rounded-medium' : 'rounded-large',
              'bg-Gray-100',
              adState === 'idle' && 'invisible',
              adState === 'loading' && 'animate-pulse',
            )}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              data-adrop-native="asset"
              src={
                AD_MOCK_MODE
                  ? `https://placehold.co/${width}x${height}/e2e8f0/64748b?text=AD`
                  : undefined
              }
              alt=""
              className="size-full border-0 object-cover"
            />
            <p data-adrop-native="headline" className="hidden" />
            <p data-adrop-native="body" className="hidden" />
          </div>
        </>
      )}
    </FrameElement>
  );
};
